import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { MetricCardSkeleton } from '../../ui/Skeleton';
import { useFormatting } from '../../../hooks/useFormatting';

/**
 * KPI Card Component
 * Large, prominent card for top-level metrics
 */
const KPICard = ({
  title,
  value,
  formatType,
  icon,
  color = 'blue',
  subtitle,
  trend,
  loading = false,
  onClick,
  metricId,
  detailPath,
  description = '', // Human-readable description for tooltip
  tooltip = '' // Tooltip text (falls back to description)
}) => {
  const { formatCurrency, formatNumber } = useFormatting();
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick(metricId || title);
    } else if (detailPath) {
      navigate(detailPath);
    } else if (metricId) {
      navigate(`/dashboard/details/${metricId}`);
    }
  };

  const getTrendIcon = () => {
    if (!trend) return null;
    if (trend > 0) return <i className="bi bi-arrow-up"></i>;
    if (trend < 0) return <i className="bi bi-arrow-down"></i>;
    return <i className="bi bi-arrow-right"></i>;
  };

  const getTrendClass = () => {
    if (!trend) return '';
    if (trend > 0) return 'positive';
    if (trend < 0) return 'negative';
    return 'neutral';
  };

  const isClickable = onClick || detailPath || metricId;
  
  return (
    <div 
      className={`kpi-card kpi-card-${color} ${isClickable ? 'kpi-card-clickable' : ''}`}
      role={isClickable ? "button" : "region"}
      aria-label={`${title} KPI${isClickable ? ' - Click to view details' : ''}`}
      onClick={isClickable ? handleClick : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      } : undefined}
    >
      {loading ? (
        <MetricCardSkeleton />
      ) : (
        <>
          <div className="kpi-header">
            <i className={`bi ${icon} kpi-icon`}></i>
            <div className="kpi-title">
              {title}
              {(tooltip || description) && (
                <span 
                  className="metric-tooltip-trigger" 
                  title={tooltip || description}
                  aria-label={`${title}: ${tooltip || description}`}
                >
                  <i className="bi bi-info-circle" aria-hidden="true"></i>
                </span>
              )}
            </div>
          </div>
          
          <div className="kpi-content">
            <div className="kpi-value">
              {(() => {
                // Ensure value is a number for formatting
                const numValue = typeof value === 'number' ? value : Number(value);
                if (formatType === 'number' && !isNaN(numValue)) return formatNumber(numValue);
                if (formatType === 'currency' && !isNaN(numValue)) return formatCurrency(numValue);
                // Fallback: return value as-is if not a valid number or no format type
                return isNaN(numValue) ? '0' : String(value);
              })()}
              {trend !== null && trend !== undefined && (
                <span className={`kpi-trend ${getTrendClass()}`}>
                  {getTrendIcon()} {Math.abs(trend).toFixed(1)}%
                </span>
              )}
            </div>
            {subtitle && <div className="kpi-subtitle">{subtitle}</div>}
          </div>
        </>
      )}
    </div>
  );
};

KPICard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  formatType: PropTypes.oneOf(['number', 'currency', null]),
  icon: PropTypes.string.isRequired,
  color: PropTypes.string,
  subtitle: PropTypes.string,
  trend: PropTypes.number,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  metricId: PropTypes.string,
  detailPath: PropTypes.string,
  description: PropTypes.string,
  tooltip: PropTypes.string
};

KPICard.defaultProps = {
  formatType: null,
  color: 'blue',
  subtitle: '',
  trend: null,
  loading: false
};

export default React.memo(KPICard);

